import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import CustomRepLaunchType from "@/utils/constants/CustomRepLaunchType.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import {QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {ReportPopupContainer} from "@/utils/presenters/interfaces.ts";
import {FromGridAction} from "@/utils/UIConstants/enums.ts";
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import CustomRepInfo = namaTypes.CustomRepInfo;
import ReportMetadataContainer = namaTypes.ReportMetadataContainer;
import GenericValue = namaTypes.GenericValue;
import PMap = namaTypes.PMap;
import ReportMetadata = namaTypes.ReportMetadata;
import DTOAbsParamsMapping = namaTypes.DTOAbsParamsMapping;

export default class EmbeddedViewUtils {

  static createEmbeddedReportPlace(reportInfo: CustomRepInfo | undefined, reportMetadata: ReportMetadataContainer | undefined, searchContextProvider: BaseDispatcher | undefined): ReportViewerPlace | ReportLauncherPlace | undefined {
    if (!reportInfo || !reportMetadata)
      return undefined;
    let mappingInfo: DTOAbsParamsMapping = reportInfo as DTOAbsParamsMapping;
    let questions: DTOFilledQuestionField[] = this.createQuestionFromMapping(searchContextProvider, mappingInfo);
    let place: ReportViewerPlace | ReportLauncherPlace = <ReportViewerPlace | ReportLauncherPlace>{};

    switch (reportInfo.launchType) {
      case CustomRepLaunchType.DirectInNewPage:
      case CustomRepLaunchType.DirectInPopup:
      case CustomRepLaunchType.Print:
        place = new ReportViewerPlace();
        this.addDefaultValueQuestions(questions, reportMetadata.metadata);
        break;
      case CustomRepLaunchType.ParamsInNewPage:
      case CustomRepLaunchType.ParamsInPopup:
        place = new ReportLauncherPlace();
        break;
    }
    place.questions = new QuestionFieldsSerializer().toURL(questions);
    place.reportId = reportInfo.reportId;
    return place;
  }

  static createQuestionFromMapping(dispatcher: BaseDispatcher | undefined, mappingInfo: DTOAbsParamsMapping): DTOFilledQuestionField[] {
    let questions: DTOFilledQuestionField[] = [];
    if (!dispatcher)
      return questions;
    let parameterValues: Record<string, GenericValue> = this.mapParameters(dispatcher, undefined, -1, mappingInfo);
    let parameterFieldMap: Record<string, string> = this.parameterFieldMap(mappingInfo);
    for (let key in parameterFieldMap) {
      let value = parameterFieldMap[key];
      if (!parameterValues[value])
        continue
      let parameterValue: GenericValue = parameterValues[value];
      questions.push({
        id: key,
        value: FieldTypesUtils.toString(parameterValue.dataType, GenericValues.getValue(parameterValue)),
        objValue: GenericValues.getValue(parameterValue)
      });
    }
    return questions;
  }

  private static mapParameters(dispatcher: BaseDispatcher, requester: string | undefined, lineNumber: number, mapping: DTOAbsParamsMapping): Record<string, GenericValue> {
    let fields: string[] = this.collectFieldsFromParameterMapping(mapping);
    let contextFields: Record<string, GenericValue> = {};
    dispatcher.addContextFieldsToMap(requester, fields, lineNumber, contextFields, FromGridAction.No, [], {});
    return contextFields;
  }

  static parameterFieldMap(mappingInfo: DTOAbsParamsMapping): Record<string, string> {
    let map: Record<string, string> = {};
    this.addParam(mappingInfo.p1, map);
    this.addParam(mappingInfo.p2, map);
    this.addParam(mappingInfo.p3, map);
    this.addParam(mappingInfo.p4, map);
    this.addParam(mappingInfo.p5, map);
    this.addParam(mappingInfo.p6, map);
    this.addParam(mappingInfo.p7, map);
    this.addParam(mappingInfo.p8, map);
    this.addParam(mappingInfo.p9, map);
    return map;
  }

  static collectFieldsFromParameterMapping(mapping: DTOAbsParamsMapping) {
    let fields = [];
    this.addField(mapping.p1, fields);
    this.addField(mapping.p2, fields);
    this.addField(mapping.p3, fields);
    this.addField(mapping.p4, fields);
    this.addField(mapping.p5, fields);
    this.addField(mapping.p6, fields);
    this.addField(mapping.p7, fields);
    this.addField(mapping.p8, fields);
    this.addField(mapping.p9, fields);
    return fields;
  }

  private static addField(p: PMap | undefined, fields: string[]) {
    if (p && p.field)
      fields.push(p.field);
  }

  private static addParam(p: PMap | undefined, map: Record<string, string>) {
    if (p && p.parameter && p.field)
      map[p.parameter] = p.field;
  }

  static overrideQuestionsIfNeeded(questionFields: DTOFilledQuestionField[], popupContainer: ReportPopupContainer | undefined) {
    let mappingInfo: DTOAbsParamsMapping = popupContainer && popupContainer.block.reportInfo ? popupContainer.block.reportInfo as DTOAbsParamsMapping : {} as DTOAbsParamsMapping
    let questionsFromMapping: DTOFilledQuestionField[] = this.createQuestionFromMapping(popupContainer?.presenter?.fetchDispatcher(), mappingInfo as DTOAbsParamsMapping);
    return this.overrideQuestions(questionsFromMapping, mappingInfo, questionFields);
  }

  static overrideQuestions(questionsFromMapping: DTOFilledQuestionField[], mappingInfo: DTOAbsParamsMapping, questionFields: namaTypes.DTOFilledQuestionField[]) {
    for (let dtoFilledQuestionField of questionsFromMapping) {
      if (this.shouldOverrideQuestion(dtoFilledQuestionField.id, mappingInfo)) {
        questionFields = questionFields.filter(l => l.id !== dtoFilledQuestionField.id);
        questionFields.push(dtoFilledQuestionField);
      }
    }
    return questionFields;
  }

  static shouldOverrideQuestion(id: string | undefined, mappingInfo: DTOAbsParamsMapping) {
    let mapping: PMap | undefined = this.findMappingByParamId(id, mappingInfo);
    if (mapping)
      return mapping.alwaysOverride;
    return false;
  }

  static findMappingByParamId(id: string | undefined, mappingInfo: DTOAbsParamsMapping): PMap | undefined {
    let ps: (PMap | undefined)[] = [mappingInfo.p1, mappingInfo.p2, mappingInfo.p3, mappingInfo.p4, mappingInfo.p5, mappingInfo.p6, mappingInfo.p7, mappingInfo.p8, mappingInfo.p9]
    for (let pMap of ps) {
      if (pMap && (pMap.parameter === id))
        return pMap;
    }
    return undefined;
  }


  static addDefaultValueQuestions(questions: DTOFilledQuestionField[], metadata: ReportMetadata | undefined) {
    if (!metadata || !metadata.metadata)
      return;
    let defaultValues: DTOFilledQuestionField[] = [];
    for (let questionFieldMetadata of metadata.metadata) {
      if (questionFieldMetadata.defaultValue) {
        defaultValues.push({
          id: questionFieldMetadata.id,
          value: questionFieldMetadata.defaultValue,
          objValue: undefined
        });
      }
    }
    for (let dtoFilledQuestionField of defaultValues) {
      let question: DTOFilledQuestionField | undefined = questions.find(l => l.id === dtoFilledQuestionField.id)
      if (ObjectChecker.isNullOrUndefined(question))
        questions.push(dtoFilledQuestionField);
    }
  }
}
